import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { documentoAPI, empresaAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Truck,
    Building,
    User,
    Clock,
    CheckCircle,
    XCircle,
    Shield,
    RefreshCw,
    Loader2,
    AlertTriangle,
    Package,
    Sun,
    Moon,
    QrCode,
    Home,
    LogOut,
    FileCheck,
    History,
    Search,
    FileText,
    BarChart3,
    Filter,
    X,
    Menu,
} from 'lucide-react';

export default function PuntoSalidaPage() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { theme, toggleTheme, isDark, setDarkMode } = useTheme();
    const [documentos, setDocumentos] = useState([]);
    const [documentosProcesados, setDocumentosProcesados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingProcesados, setLoadingProcesados] = useState(false);
    const [empresa, setEmpresa] = useState(null);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [actionType, setActionType] = useState(null);
    const [motivosSeleccionados, setMotivosSeleccionados] = useState([]);
    const [observacion, setObservacion] = useState('');
    const [processing, setProcessing] = useState(false);
    const [activeTab, setActiveTab] = useState('pendientes');
    
    // Búsqueda y filtros
    const [searchTerm, setSearchTerm] = useState('');
    const [estadoFilter, setEstadoFilter] = useState('todos');
    const [showMenu, setShowMenu] = useState(false);

    // Set dark mode for security by default
    useEffect(() => {
        setDarkMode();
    }, []);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [docsRes, empresaRes] = await Promise.all([
                documentoAPI.getAll('autorizado'),
                user?.empresa_id ? empresaAPI.getById(user.empresa_id) : Promise.resolve({ data: null }),
            ]);
            setDocumentos(docsRes.data);
            setEmpresa(empresaRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const loadProcesados = async () => {
        if (documentosProcesados.length > 0) return;
        
        setLoadingProcesados(true);
        try {
            const res = await documentoAPI.getAllAdmin();
            const procesados = res.data.filter(d => 
                ['despachado', 'rechazado'].includes(d.estado)
            );
            setDocumentosProcesados(procesados);
        } catch (err) {
            console.error('Error loading procesados:', err);
            toast.error('Error al cargar registros procesados');
        } finally {
            setLoadingProcesados(false);
        }
    };

    const handleTabChange = (value) => {
        setActiveTab(value);
        if (value === 'procesados') {
            loadProcesados();
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const openDialog = (doc, type) => {
        setSelectedDoc(doc);
        setActionType(type);
        setMotivosSeleccionados([]);
        setObservacion('');
    };

    const closeDialog = () => {
        setSelectedDoc(null);
        setActionType(null);
        setMotivosSeleccionados([]);
        setObservacion('');
    };

    const toggleMotivo = (motivo) => {
        setMotivosSeleccionados((prev) =>
            prev.includes(motivo)
                ? prev.filter((m) => m !== motivo)
                : [...prev, motivo]
        );
    };

    const handleDespacho = async () => {
        if (!selectedDoc) return;

        if (actionType === 'rechazar') {
            if (motivosSeleccionados.length === 0) {
                toast.error('Debe seleccionar al menos un motivo de rechazo');
                return;
            }
            if (!observacion.trim()) {
                toast.error('Debe ingresar una observación');
                return;
            }
        }

        setProcessing(true);
        try {
            await documentoAPI.despachar(selectedDoc.id, {
                documento_id: selectedDoc.id,
                estado: actionType === 'despachar' ? 'despachado' : 'rechazado',
                motivos_rechazo: actionType === 'rechazar' ? motivosSeleccionados : [],
                observacion: observacion || null,
            });

            toast.success(
                actionType === 'despachar'
                    ? 'Guía despachada exitosamente'
                    : 'Guía rechazada en punto de salida',
                { description: `Folio: ${selectedDoc.folio}` }
            );

            closeDialog();
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al procesar la guía');
        } finally {
            setProcessing(false);
        }
    };

    // Filtrar documentos pendientes
    const filteredPendientes = useMemo(() => {
        if (!searchTerm) return documentos;
        
        const term = searchTerm.toLowerCase();
        return documentos.filter(doc => 
            doc.folio?.toLowerCase().includes(term) ||
            doc.patente?.toLowerCase().includes(term) ||
            doc.patente_tracto?.toLowerCase().includes(term) ||
            doc.rut_destino?.toLowerCase().includes(term) ||
            doc.razon_social?.toLowerCase().includes(term) ||
            doc.destino_info?.razon_social?.toLowerCase().includes(term) ||
            doc.usuario_solicitante_nombre?.toLowerCase().includes(term) ||
            doc.conductor_nombre?.toLowerCase().includes(term) ||
            doc.conductor_rut?.toLowerCase().includes(term)
        );
    }, [documentos, searchTerm]);

    // Filtrar documentos procesados
    const filteredProcesados = useMemo(() => {
        let filtered = [...documentosProcesados];
        
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(doc => 
                doc.folio?.toLowerCase().includes(term) ||
                doc.patente?.toLowerCase().includes(term) ||
                doc.patente_tracto?.toLowerCase().includes(term) ||
                doc.rut_destino?.toLowerCase().includes(term) ||
                doc.razon_social?.toLowerCase().includes(term) ||
                doc.destino_info?.razon_social?.toLowerCase().includes(term) ||
                doc.usuario_solicitante_nombre?.toLowerCase().includes(term) ||
                doc.conductor_nombre?.toLowerCase().includes(term) ||
                doc.conductor_rut?.toLowerCase().includes(term)
            );
        }
        
        if (estadoFilter !== 'todos') {
            filtered = filtered.filter(doc => doc.estado === estadoFilter);
        }
        
        return filtered;
    }, [documentosProcesados, searchTerm, estadoFilter]);

    const clearFilters = () => {
        setSearchTerm('');
        setEstadoFilter('todos');
    };

    const motivosRechazo = empresa?.motivos_rechazo || [
        'Sello roto',
        'Carga incompleta',
        'Documentación incorrecta',
        'Vehículo no autorizado',
        'Personal no identificado',
    ];

    const nombreAreaSeguridad = empresa?.nombre_area_seguridad || 'Protección Industrial';

    return (
        <div className="min-h-screen bg-background security-view">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b-2 border-border h-16">
                <div className="flex items-center justify-between h-full px-4">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setShowMenu(!showMenu)}
                            data-testid="menu-btn"
                        >
                            <Menu className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="font-heading font-black text-lg uppercase tracking-tight">
                                {nombreAreaSeguridad}
                            </h1>
                            <p className="text-xs text-muted-foreground">Punto de Salida</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => navigate('/guias/escaner')}
                            className="border-2 border-primary"
                            data-testid="qr-scanner-btn"
                        >
                            <QrCode className="h-5 w-5 text-primary" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            data-testid="theme-toggle"
                        >
                            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={loadData}
                            data-testid="refresh-btn"
                        >
                            <RefreshCw className="h-5 w-5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleLogout}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid="logout-btn"
                        >
                            <LogOut className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </header>

            {/* Menu Dropdown */}
            {showMenu && (
                <div className="fixed top-16 left-0 right-0 z-40 bg-card border-b-2 border-border shadow-lg animate-fade-in">
                    <div className="p-4 space-y-2">
                        <Button
                            variant="ghost"
                            className="w-full justify-start gap-3"
                            onClick={() => { navigate('/home'); setShowMenu(false); }}
                        >
                            <Home className="h-5 w-5" />
                            Inicio
                        </Button>
                        <Button
                            variant="ghost"
                            className="w-full justify-start gap-3 bg-primary/10"
                            onClick={() => setShowMenu(false)}
                        >
                            <Shield className="h-5 w-5 text-primary" />
                            Punto de Salida
                        </Button>
                        <Button
                            variant="ghost"
                            className="w-full justify-start gap-3"
                            onClick={() => { navigate('/guias/todos'); setShowMenu(false); }}
                        >
                            <FileText className="h-5 w-5" />
                            Todas las Guías
                        </Button>
                        <Button
                            variant="ghost"
                            className="w-full justify-start gap-3"
                            onClick={() => { navigate('/guias/reportes'); setShowMenu(false); }}
                        >
                            <BarChart3 className="h-5 w-5" />
                            Reportes
                        </Button>
                    </div>
                </div>
            )}

            {/* Click overlay to close menu */}
            {showMenu && (
                <div 
                    className="fixed inset-0 z-30 bg-black/20"
                    onClick={() => setShowMenu(false)}
                />
            )}

            {/* Main Content */}
            <main className="pt-16 pb-4 px-4">
                {/* Stats Bar */}
                <div className="flex items-center justify-between py-4 mb-4 border-b border-border">
                    <div>
                        <p className="text-2xl font-heading font-black">{documentos.length}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">
                            Guías Pendientes
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-sm font-medium">{user?.nombre}</p>
                        <p className="text-xs text-muted-foreground">Responsable de turno</p>
                    </div>
                </div>

                {/* Buscador */}
                <Card className="border-2 mb-4">
                    <CardContent className="p-3">
                        <div className="flex flex-col gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Buscar por folio, patente, RUT, empresa, solicitante, conductor..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                    data-testid="search-input"
                                />
                            </div>
                            
                            {/* Filtros adicionales solo para tab procesados */}
                            {activeTab === 'procesados' && (
                                <div className="flex gap-2 items-center">
                                    <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                                        <SelectTrigger className="w-40" data-testid="estado-filter">
                                            <Filter className="h-4 w-4 mr-2" />
                                            <SelectValue placeholder="Estado" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="todos">Todos</SelectItem>
                                            <SelectItem value="despachado">Despachados</SelectItem>
                                            <SelectItem value="rechazado">Rechazados</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    
                                    {(searchTerm || estadoFilter !== 'todos') && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={clearFilters}
                                            className="text-muted-foreground"
                                        >
                                            <X className="h-4 w-4 mr-1" />
                                            Limpiar
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Tabs para Pendientes y Procesados */}
                <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
                    <TabsList className="grid grid-cols-2 w-full">
                        <TabsTrigger value="pendientes" className="flex items-center gap-2">
                            <FileCheck className="h-4 w-4" />
                            Pendientes ({filteredPendientes.length})
                        </TabsTrigger>
                        <TabsTrigger value="procesados" className="flex items-center gap-2">
                            <History className="h-4 w-4" />
                            Procesados ({activeTab === 'procesados' ? filteredProcesados.length : '...'})
                        </TabsTrigger>
                    </TabsList>

                    {/* Tab Pendientes */}
                    <TabsContent value="pendientes">
                        {loading ? (
                            <div className="space-y-4">
                                {[1, 2, 3].map((i) => (
                                    <Skeleton key={i} className="h-40" />
                                ))}
                            </div>
                        ) : filteredPendientes.length === 0 ? (
                            <Card className="border-2">
                                <CardContent className="py-16">
                                    <div className="empty-state">
                                        <CheckCircle className="empty-state-icon text-success" />
                                        <p className="empty-state-title">
                                            {searchTerm ? 'Sin resultados' : 'Sin guías pendientes'}
                                        </p>
                                        <p className="empty-state-description">
                                            {searchTerm 
                                                ? 'No se encontraron guías con ese criterio de búsqueda'
                                                : 'No hay guías autorizadas para verificar'}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-4">
                                {filteredPendientes.map((doc) => (
                                    <Card 
                                        key={doc.id} 
                                        className="border-2"
                                        data-testid={`security-card-${doc.folio}`}
                                    >
                                        <CardHeader className="card-industrial-header pb-3">
                                            <div className="flex items-center justify-between">
                                                <span className="folio-display text-lg">{doc.folio}</span>
                                                <StatusBadge estado={doc.estado} />
                                            </div>
                                        </CardHeader>
                                        <CardContent className="p-4 space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="p-4 bg-muted/50 rounded-sm text-center">
                                                    <Truck className="h-8 w-8 mx-auto mb-2 text-primary" />
                                                    <p className="font-mono text-2xl font-bold">
                                                        {doc.patente_tracto || doc.patente}
                                                    </p>
                                                    {doc.patente_semiremolque && (
                                                        <p className="text-xs text-muted-foreground">
                                                            Semi: {doc.patente_semiremolque}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="p-4 bg-muted/50 rounded-sm text-center">
                                                    <Building className="h-8 w-8 mx-auto mb-2 text-primary" />
                                                    <p className="font-bold truncate">
                                                        {doc.razon_social || doc.destino_info?.razon_social || 'Sin nombre'}
                                                    </p>
                                                    {doc.rut_destino && (
                                                        <p className="text-xs text-muted-foreground">{doc.rut_destino}</p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Conductor */}
                                            {doc.conductor_nombre && (
                                                <div className="p-3 bg-muted/30 rounded-sm">
                                                    <div className="flex items-center gap-2">
                                                        <User className="h-4 w-4 text-muted-foreground" />
                                                        <span className="text-sm">
                                                            <strong>Conductor:</strong> {doc.conductor_nombre}
                                                            {doc.conductor_rut && ` (${doc.conductor_rut})`}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {doc.items?.length > 0 && (
                                                <div className="p-3 bg-muted/30 rounded-sm">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Package className="h-4 w-4" />
                                                        <span className="text-sm font-bold uppercase">
                                                            Carga ({doc.items.length} items)
                                                        </span>
                                                    </div>
                                                    <ul className="text-sm space-y-1">
                                                        {doc.items.map((item, idx) => (
                                                            <li key={idx}>• {item.descripcion} - {item.cantidad} {item.unidad}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                                                <div className="flex items-center gap-2">
                                                    <User className="h-4 w-4" />
                                                    {doc.usuario_solicitante_nombre}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-4 w-4" />
                                                    {formatDate(doc.created_at)}
                                                </div>
                                            </div>

                                            {doc.aprobacion && (
                                                <div className="p-3 bg-info/10 border border-info/30 rounded-sm text-sm">
                                                    <p className="font-medium text-info">
                                                        Autorizado por: {doc.aprobacion.aprobador_nombre}
                                                    </p>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-2 gap-3 pt-2">
                                                <Button
                                                    onClick={() => openDialog(doc, 'despachar')}
                                                    className="security-mobile-btn bg-success text-success-foreground hover:bg-success/90"
                                                    data-testid={`despachar-${doc.folio}`}
                                                >
                                                    <CheckCircle className="h-6 w-6" />
                                                    DESPACHAR
                                                </Button>
                                                <Button
                                                    onClick={() => openDialog(doc, 'rechazar')}
                                                    variant="outline"
                                                    className="security-mobile-btn border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                                    data-testid={`rechazar-${doc.folio}`}
                                                >
                                                    <XCircle className="h-6 w-6" />
                                                    RECHAZAR
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Tab Procesados */}
                    <TabsContent value="procesados">
                        {loadingProcesados ? (
                            <div className="space-y-4">
                                {[1, 2, 3].map((i) => (
                                    <Skeleton key={i} className="h-32" />
                                ))}
                            </div>
                        ) : filteredProcesados.length === 0 ? (
                            <Card className="border-2">
                                <CardContent className="py-16">
                                    <div className="empty-state">
                                        <History className="empty-state-icon text-muted-foreground" />
                                        <p className="empty-state-title">
                                            {searchTerm || estadoFilter !== 'todos' ? 'Sin resultados' : 'Sin registros procesados'}
                                        </p>
                                        <p className="empty-state-description">
                                            {searchTerm || estadoFilter !== 'todos'
                                                ? 'No se encontraron guías con los filtros aplicados'
                                                : 'Los registros procesados aparecerán aquí'}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-3">
                                {filteredProcesados.map((doc) => (
                                    <Card 
                                        key={doc.id} 
                                        className="border cursor-pointer hover:bg-muted/50 transition-colors"
                                        onClick={() => navigate(`/guias/documento/${doc.id}`)}
                                        data-testid={`procesado-card-${doc.folio}`}
                                    >
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <span className="font-mono font-bold">{doc.folio}</span>
                                                        <StatusBadge estado={doc.estado} />
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <Truck className="h-3 w-3" />
                                                            {doc.patente_tracto || doc.patente}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Building className="h-3 w-3" />
                                                            {doc.razon_social || doc.destino_info?.razon_social || '-'}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <User className="h-3 w-3" />
                                                            {doc.usuario_solicitante_nombre}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {formatDate(doc.created_at)}
                                                    </p>
                                                    {doc.punto_salida?.observacion && (
                                                        <p className="text-xs text-muted-foreground mt-1 italic">
                                                            "{doc.punto_salida.observacion}"
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </main>

            {/* Action Dialog */}
            <Dialog open={!!selectedDoc} onOpenChange={() => closeDialog()}>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">
                            {actionType === 'despachar' ? 'Confirmar Despacho' : 'Rechazar en Punto de Salida'}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedDoc?.folio} - {selectedDoc?.patente_tracto || selectedDoc?.patente}
                        </DialogDescription>
                    </DialogHeader>

                    <ScrollArea className="flex-1 pr-4">
                        <div className="space-y-4 py-4">
                            {actionType === 'despachar' ? (
                                <>
                                    <div className="p-4 bg-success/10 border border-success/30 rounded-sm">
                                        <div className="flex items-center gap-3">
                                            <CheckCircle className="h-8 w-8 text-success" />
                                            <div>
                                                <p className="font-bold text-success">Verificación Completa</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Confirme que la carga física corresponde a la guía
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="input-label">Observación (opcional)</Label>
                                        <Textarea
                                            value={observacion}
                                            onChange={(e) => setObservacion(e.target.value)}
                                            placeholder="Notas adicionales..."
                                            className="min-h-[80px] border-2"
                                            data-testid="observacion-input"
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-sm">
                                        <div className="flex items-center gap-3">
                                            <AlertTriangle className="h-8 w-8 text-destructive" />
                                            <div>
                                                <p className="font-bold text-destructive">Rechazo en Punto de Salida</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Se notificará al gerente y solicitante
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <Label className="input-label">
                                            Motivos de Rechazo <span className="text-destructive">*</span>
                                        </Label>
                                        <div className="space-y-2">
                                            {motivosRechazo.map((motivo) => (
                                                <div
                                                    key={motivo}
                                                    className={cn(
                                                        'flex items-center gap-3 p-3 rounded-sm border-2 cursor-pointer transition-colors',
                                                        motivosSeleccionados.includes(motivo)
                                                            ? 'border-destructive bg-destructive/10'
                                                            : 'border-border hover:border-muted-foreground'
                                                    )}
                                                    onClick={() => toggleMotivo(motivo)}
                                                    data-testid={`motivo-${motivo.replace(/\s+/g, '-').toLowerCase()}`}
                                                >
                                                    <Checkbox
                                                        checked={motivosSeleccionados.includes(motivo)}
                                                        onCheckedChange={() => toggleMotivo(motivo)}
                                                    />
                                                    <span className="font-medium">{motivo}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="input-label">
                                            Observación <span className="text-destructive">*</span>
                                        </Label>
                                        <Textarea
                                            value={observacion}
                                            onChange={(e) => setObservacion(e.target.value)}
                                            placeholder="Describa el problema encontrado..."
                                            className="min-h-[100px] border-2"
                                            data-testid="observacion-rechazo-input"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </ScrollArea>

                    <DialogFooter className="pt-4 border-t">
                        <Button
                            variant="outline"
                            onClick={closeDialog}
                            className="border-2"
                            data-testid="cancel-dialog-btn"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleDespacho}
                            disabled={processing}
                            className={`btn-industrial ${
                                actionType === 'despachar'
                                    ? 'bg-success text-success-foreground'
                                    : 'bg-destructive text-destructive-foreground'
                            }`}
                            data-testid="confirm-action-btn"
                        >
                            {processing ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : actionType === 'despachar' ? (
                                <>
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    Confirmar Despacho
                                </>
                            ) : (
                                <>
                                    <XCircle className="h-5 w-5 mr-2" />
                                    Confirmar Rechazo
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
